import React, { useCallback, useContext, useEffect, useState } from "react";
import axios from "axios";
import { useToast } from "@chakra-ui/react";
import {
  requestForToken,
  deleteFcmToken,
  onMessageListener,
} from "../firebase";
import { update_fcm_token_url } from "../utils/constants";

const STORAGE_KEY = "fcm_notifications_enabled";

const authHeaders = () => ({
  headers: { Authorization: "Bearer " + localStorage.getItem("acces_token") },
});

const NotificationContext = React.createContext();

export const NotificationProvider = ({ children }) => {
  const [isEnabled, setIsEnabled] = useState(
    localStorage.getItem(STORAGE_KEY) === "true" &&
      typeof Notification !== "undefined" &&
      Notification.permission === "granted",
  );
  const toast = useToast();

  // Keep the token fresh on reload if the admin already enabled notifications before
  useEffect(() => {
    if (!isEnabled) return;
    requestForToken().then(async (token) => {
      if (!token) return;
      try {
        await axios.post(
          update_fcm_token_url,
          { fcmToken: token },
          authHeaders(),
        );
      } catch (error) {
        console.error("Failed to sync FCM token with backend:", error);
      }
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    const unsubscribe = onMessageListener((payload) => {
      toast({
        title: payload?.notification?.title || "Nouvelle Commande",
        description:
          payload?.notification?.body || "Une nouvelle commande a été ajoutée",
        status: "success",
        duration: 9000,
        isClosable: true,
        position: "top-right",
      });
    });
    return () => {
      if (unsubscribe) unsubscribe();
    };
  }, [toast]);

  const enableNotifications = useCallback(async () => {
    const token = await requestForToken();
    if (!token) {
      const blocked =
        typeof Notification !== "undefined" &&
        Notification.permission === "denied";
      toast({
        title: blocked
          ? "Notifications bloquées"
          : "Impossible d'activer les notifications",
        description: blocked
          ? "Autorisez les notifications pour ce site dans les paramètres de votre navigateur."
          : "Veuillez réessayer.",
        status: "error",
        duration: 6000,
        isClosable: true,
      });
      return;
    }
    try {
      await axios.post(
        update_fcm_token_url,
        { fcmToken: token },
        authHeaders(),
      );
      localStorage.setItem(STORAGE_KEY, "true");
      setIsEnabled(true);
      toast({
        title: "Notifications activées",
        status: "success",
        duration: 4000,
        isClosable: true,
      });
    } catch (error) {
      console.error("Failed to sync FCM token with backend:", error);
      toast({
        title: "Erreur lors de l'activation des notifications",
        status: "error",
        duration: 5000,
        isClosable: true,
      });
    }
  }, [toast]);

  const disableNotifications = useCallback(async () => {
    await deleteFcmToken();
    try {
      await axios.delete(update_fcm_token_url, authHeaders());
    } catch (error) {
      console.error("Failed to clear FCM token on backend:", error);
    }
    localStorage.setItem(STORAGE_KEY, "false");
    setIsEnabled(false);
    toast({
      title: "Notifications désactivées",
      status: "info",
      duration: 4000,
      isClosable: true,
    });
  }, [toast]);

  const toggleNotifications = useCallback(() => {
    return isEnabled ? disableNotifications() : enableNotifications();
  }, [isEnabled, enableNotifications, disableNotifications]);

  return (
    <NotificationContext.Provider value={{ isEnabled, toggleNotifications }}>
      {children}
    </NotificationContext.Provider>
  );
};

export const useNotificationContext = () => useContext(NotificationContext);
